import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserContext, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, setUser, userLoading } = useContext(UserContext);
  const [checking, setChecking] = useState(true);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (userLoading) return;
    
    const sessionId = searchParams.get("session_id");
    if (!sessionId || !user) {
      navigate("/");
      return;
    }

    checkPaymentStatus(sessionId);
  }, [searchParams, user, userLoading]);

  const checkPaymentStatus = async (sessionId, attempt = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempt >= maxAttempts) {
      setStatus("timeout");
      setChecking(false);
      toast.error("Payment verification timed out. Please check your email.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${API}/subscription/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.payment_status === "paid") {
        setStatus("success");
        setChecking(false);
        
        const userRes = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(userRes.data);
        
        toast.success("Payment successful! Welcome to Eternal Now Premium!");
      } else if (res.data.status === "expired") {
        setStatus("expired");
        setChecking(false);
        toast.error("Payment session expired. Please try again.");
      } else {
        setTimeout(() => checkPaymentStatus(sessionId, attempt + 1), pollInterval);
      }
    } catch (error) {
      setStatus("error");
      setChecking(false);
      toast.error("Failed to verify payment status");
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-8">
      <div className="max-w-lg w-full border border-white/10 p-12 text-center space-y-8">
        {checking ? (
          <>
            <Loader2 className="w-16 h-16 text-white mx-auto animate-spin" />
            <h2 className="text-sm uppercase tracking-widest font-light">Verifying Payment...</h2>
            <p className="text-xs text-gray-600 font-light">Please wait</p>
          </>
        ) : status === "success" ? (
          <>
            <div className="w-20 h-20 mx-auto border border-white/30 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <h2 className="script-font text-5xl">Thank You</h2>
            <p className="text-sm text-gray-400 font-light">Your premium subscription is now active</p>
            
            <div className="text-xs uppercase tracking-wider text-gray-600 pt-6">
              Premium Member
            </div>

            <Button
              data-testid="start-browsing-btn"
              onClick={() => navigate("/browse")}
              className="w-full bg-white text-black hover:bg-gray-200 py-6 text-sm uppercase tracking-wider font-light mt-8"
            >
              Start Browsing
            </Button>
          </>
        ) : (
          <>
            <div className="w-20 h-20 mx-auto border border-white/30 flex items-center justify-center">
              <span className="text-white text-3xl">×</span>
            </div>
            <h2 className="script-font text-5xl">Payment {status === "expired" ? "Expired" : "Failed"}</h2>
            <p className="text-sm text-gray-400 font-light">
              {status === "expired" 
                ? "Your payment session has expired"
                : "We couldn't verify your payment"}
            </p>

            <Button
              data-testid="try-again-btn"
              onClick={() => navigate("/browse")}
              className="w-full bg-white text-black hover:bg-gray-200 py-6 text-sm uppercase tracking-wider font-light mt-8"
            >
              Try Again
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default SuccessPage;